import { useMemo } from 'react';
import { confirmsByHour, dailyStats } from '@chemoflow/domain';
import { Icon } from '../components/Icon.js';
import {
  download,
  exportName,
  patientRows,
  toCSV,
  toExcelXml,
  PATIENT_HEAD,
  auditRows,
} from '../lib/export.js';
import { useAudit } from '../api/hooks.js';
import { useBoardData } from '../state/board.js';
import { useClock } from '../state/clock.js';

const TILE_COLOR = {
  confirm: 'var(--primary)',
  change: 'var(--change)',
  stop: 'var(--stop)',
  start: 'var(--start)',
  admin: 'var(--admin)',
} as const;

function Tile({
  label,
  value,
  unit,
  color,
}: {
  label: string;
  value: number | null;
  unit: string;
  color?: string;
}) {
  return (
    <div className="tile" style={{ '--c': color ?? 'var(--idle)' } as React.CSSProperties}>
      <small>
        {color ? <i /> : null}
        {label}
      </small>
      <b>
        {value ?? '―'}
        <em>{value == null ? '' : unit}</em>
      </b>
    </div>
  );
}

/** 集計・出力。効果測定（要件13-3）の手集計を助けるための画面。 */
export function StatsView() {
  const { cases } = useBoardData();
  const { now } = useClock();
  const audit = useAudit(true);

  const stats = useMemo(() => dailyStats(cases), [cases]);
  const { hours, counts } = useMemo(
    () => confirmsByHour(cases, (ms) => new Date(ms).getHours()),
    [cases],
  );
  const max = Math.max(1, ...counts);

  const exportCsv = () =>
    download(`${exportName(now)}.csv`, toCSV([PATIENT_HEAD, ...patientRows(cases)]), 'text/csv');

  const exportXls = () =>
    download(`${exportName(now)}.xls`, toExcelXml(stats, cases), 'application/vnd.ms-excel');

  const exportLog = () => {
    const data = audit.data;
    if (!data) return;
    download(
      `${exportName(now, '操作ログ')}.csv`,
      toCSV([
        ['時刻', '操作者', '職種', '患者ID', '氏名', '操作'],
        ...auditRows(data.entries, data.patients),
      ]),
      'text/csv',
    );
  };

  return (
    <main className="board is-one view" id="view-stats" data-view="stats">
      <section id="stats">
        <div className="tiles">
          <Tile label="本日の患者" value={stats.total} unit="人" />
          <Tile label="調製確定" value={stats.confirm} unit="件" color={TILE_COLOR.confirm} />
          <Tile label="調製変更" value={stats.change} unit="件" color={TILE_COLOR.change} />
          <Tile label="調製中止" value={stats.stop} unit="件" color={TILE_COLOR.stop} />
          <Tile
            label="調製開始（調製件数）"
            value={stats.start}
            unit="件"
            color={TILE_COLOR.start}
          />
          <Tile label="調製完了" value={stats.made} unit="件" color={TILE_COLOR.start} />
          <Tile label="投与開始" value={stats.admin} unit="件" color={TILE_COLOR.admin} />
          <Tile label="投与完了" value={stats.done} unit="件" color={TILE_COLOR.admin} />
          <Tile label="処方箋（Rp）枚数" value={stats.rp} unit="枚" />
          <Tile label="確定 → 調製開始 平均" value={stats.toStartMin} unit="分" />
          <Tile label="調製開始 → 投与開始 平均" value={stats.toAdminMin} unit="分" />
        </div>

        <div className="panel">
          <h2>
            時間帯ごとの確定件数 <small>10時前後の集中を見るために</small>
          </h2>
          <div className="bars">
            {hours.map((h, i) => (
              <div key={h} className={`bar ${counts[i] ? '' : 'is-zero'}`.trim()}>
                <b>{counts[i] || ''}</b>
                <i style={{ height: `${((counts[i] ?? 0) / max) * 120}px` }} />
                <small>{h}時</small>
              </div>
            ))}
          </div>
        </div>

        <div className="panel">
          <h2>
            出力 <small>患者ごとの記録と集計を書き出します</small>
          </h2>
          <div className="btns">
            <button className="pill-btn" type="button" onClick={exportXls}>
              <Icon name="download" />
              Excelで出力
            </button>
            <button className="pill-btn" type="button" onClick={exportCsv}>
              <Icon name="download" />
              CSVで出力
            </button>
            <button
              className="pill-btn ghost"
              type="button"
              onClick={exportLog}
              disabled={!audit.data}
            >
              <Icon name="download" />
              操作ログ（CSV）
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
